/**
 * DrawerTopUpPrompt — the remedy for an empty drawer, offered in place.
 *
 * Owner-approved 2026-08-29. Every door that pays money OUT (Fix Receipt,
 * Return/Refund, the terminal stock correction, and later the supplier
 * payment doors) refuses when the cashier drawer is short — and until now
 * every one of them dead-ended: leave the screen, walk to Fund Management,
 * make a transfer, come back, redo the work. With a customer waiting, that
 * is exactly the moment somebody decides to settle it outside the system.
 *
 * This component catches the shared `insufficient_funds` refusal
 * (utils/helpers.update_cashier_wallet — same shape PO payment has always
 * returned) and offers the Safe → Cashier transfer inline, then retries the
 * original action.
 *
 * Two owner rulings are load-bearing here:
 *   1. NO WARM PIN. Moving money between vaults is its own act, never a
 *      continuation of the correction — a manager who approved a fix and
 *      walked away must not have their warm window spend safe money. The
 *      PIN field is always empty, never pre-filled, and is cleared after
 *      every attempt.
 *   2. OVERSHOOT IS ALLOWED. The shortfall is arithmetic; the drawer's real
 *      problem is making up the amount in actual notes and coins. We suggest
 *      the shortfall and let the human type reality (₱200 short → take
 *      ₱1,000). Never less than the shortfall, or the retry just fails again.
 */
import React, { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Banknote, Shield, ArrowRight, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const peso = (n) => `₱${Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2 })}`;

/**
 * @param {object}  detail    the insufficient_funds payload from the server
 * @param {string}  branchId
 * @param {string}  linkRef   document this unblocks (e.g. "COR-MB-000123",
 *                            "RMA-000045") — written into the transfer note
 *                            so Fund Management shows WHY the money moved
 * @param {func}    onFunded  called after a successful transfer (retry here)
 * @param {func}    onCancel
 */
export default function DrawerTopUpPrompt({ detail, branchId, linkRef = '', onFunded, onCancel }) {
  const shortfall = Number(detail?.shortfall || 0);
  const safeBalance = Number(detail?.safe_balance || 0);
  const canUseSafe = detail?.can_use_safe !== false && safeBalance >= shortfall && shortfall > 0;

  const [amount, setAmount] = useState(shortfall ? String(shortfall) : '');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  const amt = parseFloat(amount) || 0;
  const tooLittle = amt > 0 && amt < shortfall;
  const tooMuch = amt > safeBalance;

  const runTransfer = async () => {
    if (amt <= 0) { toast.error('Enter how much to take from the Safe.'); return; }
    if (tooLittle) { toast.error(`Take at least ${peso(shortfall)} — less and the refund still cannot be paid.`); return; }
    if (tooMuch) { toast.error(`The Safe only holds ${peso(safeBalance)}.`); return; }
    if (!pin.trim()) { toast.error('Manager PIN is required to move money from the Safe.'); return; }
    setBusy(true);
    try {
      await api.post(`${BACKEND_URL}/api/fund-transfers`, {
        branch_id: branchId,
        transfer_type: 'safe_to_cashier',
        amount: amt,
        note: linkRef ? `Drawer top-up for ${linkRef}` : 'Drawer top-up for a refund',
        manager_pin: pin.trim(),
      });
      toast.success(`${peso(amt)} moved from the Safe to the drawer — retrying.`);
      setPin('');                      // ruling 1: never keep the credential
      onFunded?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Transfer failed'), { duration: 9000 });
      setPin('');                      // a failed PIN is never reused either
    }
    setBusy(false);
  };

  return (
    <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 space-y-3 text-xs"
         data-testid="drawer-topup-prompt">
      <div className="flex items-start gap-2">
        <AlertTriangle size={15} className="text-amber-600 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-amber-900">The drawer is short {peso(shortfall)}</div>
          <div className="text-amber-800 mt-0.5">
            Drawer holds {peso(detail?.cashier_balance)} · this needs {peso(detail?.required)}.
          </div>
        </div>
      </div>

      {!canUseSafe ? (
        <div className="text-amber-900">
          The Safe holds {peso(safeBalance)}, which is not enough either. Add cash to the
          Safe (Fund Management → Capital Injection) before paying this out.
          {onCancel && (
            <div className="mt-2">
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onCancel}>Close</Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-amber-900">
            <Banknote size={13} />
            <span>Safe → Drawer</span>
            <ArrowRight size={11} />
            <span>Safe holds {peso(safeBalance)}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-amber-900">Amount to take *</Label>
              <Input value={amount} onChange={(e) => setAmount(e.target.value)}
                type="number" min={shortfall} step="0.01" className="mt-1 h-8 text-xs"
                data-testid="drawer-topup-amount" />
              <p className="text-[10px] text-amber-700 mt-0.5">
                At least {peso(shortfall)} — take more if you need notes and coins to make it up.
              </p>
              <div className="flex gap-1 mt-1">
                {[shortfall, 500, 1000, 5000]
                  .filter((v, i, a) => v >= shortfall && a.indexOf(v) === i && v <= safeBalance)
                  .slice(0, 4)
                  .map((v) => (
                    <button key={v} type="button" onClick={() => setAmount(String(v))}
                      className="px-2 py-0.5 rounded border border-amber-300 bg-white text-[10px] text-amber-900 hover:bg-amber-100"
                      data-testid={`drawer-topup-quick-${v}`}>
                      {peso(v)}
                    </button>
                  ))}
              </div>
            </div>
            <div>
              <Label className="text-[11px] text-amber-900 flex items-center gap-1">
                <Shield size={11} /> Manager PIN *
              </Label>
              {/* Ruling 1: always empty, never pre-filled from a warm session. */}
              <Input type="password" autoComplete="one-time-code" data-lpignore="true"
                value={pin} onChange={(e) => setPin(e.target.value)}
                placeholder="Enter manager PIN" className="mt-1 h-8 text-xs"
                onKeyDown={(e) => e.key === 'Enter' && !busy && runTransfer()}
                data-testid="drawer-topup-pin" />
              <p className="text-[10px] text-amber-700 mt-0.5">
                Required every time — moving vault money is its own approval.
              </p>
            </div>
          </div>

          {tooLittle && (
            <div className="text-[11px] text-red-700">
              Less than the shortfall — the payout would fail again.
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={runTransfer} disabled={busy || !pin.trim() || amt <= 0 || tooLittle || tooMuch}
              className="h-7 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="drawer-topup-submit">
              {busy ? 'Moving…' : `Take ${peso(amt)} from the Safe`}
            </Button>
            {onCancel && (
              <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={onCancel}
                data-testid="drawer-topup-cancel">Cancel</Button>
            )}
          </div>
          <p className="text-[10px] text-amber-700">
            Recorded as a Safe → Cashier transfer{linkRef ? ` linked to ${linkRef}` : ''} —
            it appears in Fund Management and tonight's expected drawer.
          </p>
        </>
      )}
    </div>
  );
}
